import React, { useRef, useEffect } from 'react';

import {
  forEach,
  isArray,
  isObject,
  camelCase,
  snakeCase,
  capitalize,
  isEmpty,
  differenceWith
} from 'lodash';

import {
  INPUT_VALUE_DATEFORMAT,
  displayDateReportFormat,
  displayMonthAndYearFormat
} from './CommonConstants';
import { toast } from 'react-toastify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import AppConstants from '/src/core-components/AppConstants';
import moment from 'moment';
import Image from '../core-components/Image/Image';
import { ADMIN_URL } from '../core-components/Utilities/CommonConstants';

export const getTrimmedValue = (value, withSpace) => {
  if (value) {
    return withSpace ? value.trim().replace(/\s\s+/g, ' ') : value.trim().replace(/\s+/g, '');
  } else {
    return '';
  }
};

export const getErrorMessage = (error) => {
  if (error?.response?.data || error?.data || (error?.errors && error?.errors?.length)) {
    const errorObject =
      (error?.data && error?.data?.errorMessage) ||
      error?.response?.data?.message ||
      error?.response?.data?.errorMsg ||
      (Array.isArray(error?.response?.data?.errors) &&
        error?.response?.data?.errors?.length &&
        error?.response?.data?.errors[0]?.msg) ||
      error?.response?.data?.msg ||
      error?.response?.data ||
      error?.errors[0]?.msg ||
      'Something went wrong.';

    switch (typeof errorObject) {
      case 'object':
        return (
          errorObject?.msg ||
          JSON.stringify(errorObject.msg) ||
          errorObject?.data?.msg ||
          errorObject?.resp?.msg
        );
      case 'string':
        return errorObject;
      default:
        return 'Something went wrong.';
    }
  } else {
    if (typeof error === 'string') {
      return error;
    }
  }
};

// Toast Error with ID to avoid duplicate messages

export const getMessageIdForToast = (message) => {
  if (message) return message.replace(/ +/g, '_').toLowerCase();
  return '';
};

export const errToastMessage = (err) => {
  const message = getErrorMessage(err);

  if (message) {
    const id = getMessageIdForToast(message);
    toast.error(message, { toastId: id });
  }
};

export const toSnakeCase = (camel_case_object) => {
  var snakeCaseObject = {};
  forEach(camel_case_object, function (value, key) {
    if (isArray(value)) {
      value = value.map((v) => {
        return isObject(v) ? toSnakeCase(v) : v;
      });
    } else if (isObject(value)) {
      value = toSnakeCase(value);
    }
    snakeCaseObject[snakeCase(key)] = value;
  });
  return snakeCaseObject;
};

export const toCamelCase = (snake_case_object) => {
  let camelCaseObject = {};
  forEach(snake_case_object, function (value, key) {
    if (isArray(value)) {
      value = value.map((v) => {
        return isObject(v) ? toCamelCase(v) : v;
      });
    } else if (isObject(value)) {
      value = toCamelCase(value);
    }
    camelCaseObject[camelCase(key)] = value;
  });
  return camelCaseObject;
};

export const copyToClipboard = (text, message) => {
  const type = 'text/plain';
  const blob = new Blob([text], { type });
  if (!navigator?.clipboard || !navigator?.clipboard?.write) {
    // Clipboard API not supported in this browser
    errToastMessage('Clipboard API is not supported in this browser!');
    return;
  }
  const data = [new ClipboardItem({ [type]: blob })];
  try {
    navigator?.clipboard.write(data).then(() => {
      toast.info(message);
    });
  } catch (error) {
    errToastMessage('Copying was unsuccessful!');
  }
};

/**
 * Validates phone number
 * case 1: if input given contains intl number(eg:+15417543010),checks for validity
 * case 2: if local string(eg:9892782982) is given parses the number with default code(i.e india) and checks validity
 * @param   {string} phoneNumber
 * @return  {boolean}
 */
export const isValidPhoneNumber = (value) => {
  const regexForTenDigit = /^[6-9]{1}[0-9]{9}/;
  const regexForAllowedCharacter = /^[0-9+]+$/;
  try {
    let parsedPhone = null;
    let country;
    let phone = value.toString();

    // Numbers without country codes ( assuming indian numbers )
    if (phone[0] !== '+' && Number.isInteger(parseInt(phone[0], 10))) {
      if (phone.length === 10 && phone.match(regexForTenDigit)) {
        phone = `+91${phone}`;
      } else if (phone[0] === '0' && phone.slice(1).match(regexForTenDigit)) {
        phone = `+91${phone.slice(1)}`;
      } else {
        return false;
      }
    }
    if (phone.slice(0, 3) === '+91') {
      // library was allowing indian numbers with less than 10 digits
      if (!phone.slice(3).match(regexForTenDigit)) {
        return false;
      }
      country = 'IN';
    }

    if (!phone.match(regexForAllowedCharacter)) {
      return false;
    }
    // parse number ( parses numbers in specific formats also e.g. (+91) 123 456 7890 )
    // also parses numbers with different country codes
    parsedPhone = parsePhoneNumberFromString(phone, country);

    // validate the parsed number
    if (parsedPhone == undefined || (parsedPhone && !parsedPhone.isValid())) {
      return false;
    }

    return true;
  } catch (err) {
    // try catch block was required because parsePhoneNumber throws an error if number is invalid
    return false;
  }
};

export const jwtParser = (jwtToken) => {
  try {
    return JSON.parse(atob(jwtToken.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getCustomCheckSVG = (
  color = '#999999',
  width = '35px',
  height = '18px',
  cursor = 'default'
) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 94 94'
      fill={'none'}
      style={{ display: 'inline-block', verticalAlign: 'middle', cursor: cursor }}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M54.6924 93.1533H39.3077C38.1539 93.1533 37.3847 92.3838 37.3847 91.2305V86.2305C33.3462 85.2686 29.6923 83.7305 26.0385 81.6152L22.577 85.0762C21.8077 85.8457 20.6539 85.8457 19.8847 85.0762L8.9231 74.1152C8.1539 73.3457 8.1539 72.1924 8.9231 71.4229L12.3847 67.9609C10.2693 64.3076 8.7308 60.6533 7.7693 56.6152H2.76927C1.61542 56.6152 0.846191 55.8457 0.846191 54.6924V39.3076C0.846191 38.1533 1.61542 37.3838 2.76927 37.3838H7.7693C8.7308 33.3457 10.2693 29.6924 12.3847 26.0381L8.9231 22.5762C8.1539 21.8076 8.1539 20.6533 8.9231 19.8838L19.8847 8.92285C20.077 8.53809 20.6539 8.3457 21.2308 8.3457C21.8077 8.3457 22.1923 8.53809 22.577 8.92285L26.0385 12.3838C29.6923 10.2686 33.3462 8.73047 37.3847 7.76855V2.76855C37.3847 1.61523 38.1539 0.845703 39.3077 0.845703H54.6924C55.8462 0.845703 56.6154 1.61523 56.6154 2.76855V7.76855C60.6539 8.73047 64.3077 10.2686 67.9616 12.3838L71.4231 8.92285C72.1924 8.15332 73.3462 8.15332 74.1154 8.92285L85.077 19.8838C85.8462 20.6533 85.8462 21.8076 85.077 22.5762L81.6154 26.0381C83.7308 29.5 85.2693 33.3457 86.2308 37.3838H91.2308C92.3847 37.3838 93.1539 38.1533 93.1539 39.3076V54.6924C93.1539 55.8457 92.3847 56.6152 91.2308 56.6152H86.2308C85.2693 60.6533 83.7308 64.3076 81.6154 67.9609L85.077 71.4229C85.8462 72.1924 85.8462 73.3457 85.077 74.1152L74.1154 85.0762C73.3462 85.8457 72.1924 85.8457 71.4231 85.0762L67.9616 81.6152C64.5 83.7305 60.6539 85.2686 56.6154 86.2305V91.2305C56.6154 92.3838 55.8462 93.1533 54.6924 93.1533ZM41.4231 89.3076H52.7693V84.6924C52.7693 83.7305 53.3462 82.9609 54.3077 82.7686C58.9231 81.8076 63.1539 80.0762 67.1924 77.3838C67.9616 76.8076 68.9231 77 69.6924 77.5762L72.9616 80.8457L81.0385 72.7686L77.7693 69.499C77.1924 68.9229 77 67.7686 77.577 67C80.2693 63.1533 82 58.7305 82.9616 54.1152C83.1539 53.1533 83.9231 52.5762 84.8847 52.5762H89.3077V41.0381H84.6924C83.7308 41.0381 82.9616 40.4609 82.7693 39.499C81.8077 34.8838 80.077 30.6533 77.3847 26.6152C76.8077 25.8457 77 24.8838 77.577 24.1152L80.8462 20.8457L72.7693 12.7686L69.5 16.2305C68.9231 16.8076 67.7693 16.999 67 16.4229C63.1539 13.7305 58.7308 11.999 54.1154 11.0381C53.1539 10.8457 52.577 10.0762 52.577 9.11523V4.69141H41.0385V9.30762C41.0385 10.2686 40.4616 11.0381 39.5 11.2305C34.8847 12.1924 30.6539 13.9229 26.6154 16.6152C25.8462 17.1924 24.8847 16.999 24.1154 16.4229L20.8462 13.1533L12.7693 21.2305L16.0385 24.499C16.6154 25.0762 16.8077 26.2305 16.2308 27C13.5385 30.8457 11.8077 35.2686 10.8462 39.8838C10.6539 40.8457 9.8847 41.4229 8.9231 41.4229H4.69235V52.9609H9.30771C10.2693 52.9609 11.0385 53.5381 11.2308 54.499C12.1923 59.1152 13.9231 63.3457 16.6154 67.3838C17.1923 68.1533 17 69.1152 16.4231 69.8838L13.1539 73.1533L21.2308 81.2305L24.5 77.9609C25.077 77.3838 26.2308 77.1924 27 77.7686C30.8462 80.4609 35.2693 82.1924 39.8847 83.1533C40.8462 83.3457 41.4231 84.1152 41.4231 85.0762V89.3076ZM47.0009 75.8438C31.0394 75.8438 18.1548 62.959 18.1548 46.9971C18.1548 31.0361 31.0394 18.1514 47.0009 18.1514C62.9625 18.1514 75.8471 31.0361 75.8471 46.9971C75.8471 62.959 62.9625 75.8438 47.0009 75.8438ZM47.0009 21.9971C33.1548 21.9971 22.0009 33.1514 22.0009 46.9971C22.0009 60.8438 33.1548 71.9971 47.0009 71.9971C60.8471 71.9971 72.0009 60.8438 72.0009 46.9971C72.0009 33.1514 60.8471 21.9971 47.0009 21.9971ZM61.2314 40.3301C61.2314 42.4824 59.703 44.2754 57.6734 44.6885V62.5664H55.0054V44.4033C53.4366 43.7158 52.3374 42.1533 52.3374 40.3311C52.3374 38.5088 53.4366 36.9463 55.0054 36.2588V31.4365H57.6734V35.9727C59.7034 36.3857 61.2314 38.1787 61.2314 40.3301ZM56.7842 38.5518C55.8038 38.5518 55.0054 39.3506 55.0054 40.3311C55.0054 41.3115 55.8038 42.1094 56.7842 42.1094C57.7646 42.1094 58.563 41.3115 58.563 40.3311C58.563 39.3506 57.7646 38.5518 56.7842 38.5518ZM41.6636 53.6719C41.6636 51.5205 40.1356 49.7275 38.1064 49.3145V31.4365H35.4376V49.5996C33.8688 50.2871 32.7696 51.8496 32.7696 53.6719C32.7696 55.4941 33.8688 57.0566 35.4376 57.7441V62.5664H38.1064V58.0293C40.1356 57.6172 41.6636 55.8232 41.6636 53.6719ZM35.438 53.6719C35.438 52.6914 36.2364 51.8926 37.2168 51.8926C38.1972 51.8926 38.9956 52.6914 38.9956 53.6719C38.9956 54.6533 38.1972 55.4502 37.2168 55.4502C36.2364 55.4502 35.438 54.6533 35.438 53.6719ZM47.8903 42.4844C49.4583 43.1719 50.5583 44.7344 50.5583 46.5566C50.5583 48.3789 49.4583 49.9414 47.8903 50.6289V62.5664H45.2215V50.9141C43.1923 50.501 41.6635 48.708 41.6635 46.5566C41.6635 44.4053 43.1927 42.6113 45.2215 42.1992V31.4365H47.8903V42.4844ZM46.1111 44.7773C45.1299 44.7773 44.3323 45.5762 44.3323 46.5566C44.3323 47.5371 45.1299 48.3359 46.1111 48.3359C47.0915 48.3359 47.8899 47.5371 47.8899 46.5566C47.8899 45.5762 47.0915 44.7773 46.1111 44.7773Z'
        fill={color}
      />
    </svg>
  );
};

export const getActiveCheckboxIcon = () => {
  return <Image name='ActiveCheckbox.svg' />;
};

export const getInActiveCheckboxIcon = () => {
  return <Image name='InActiveCheckbox.svg' />;
};

export const getActiveIcon = (type, state) => {
  switch (type) {
    case 'identity':
      return state === 'active' ? (
        <Image name='ActiveIdentity.svg' />
      ) : (
        <Image name='InActiveIdentity.svg' />
      );
    case 'court':
      return state === 'active' ? (
        <Image name='ActiveCourt.svg' />
      ) : (
        <Image name='InActiveCourt.svg' />
      );
    case 'address':
      return state === 'active' ? (
        <Image name='ActiveAddress.svg' />
      ) : (
        <Image name='InActiveAddress.svg' />
      );
    case 'employment':
      return state === 'active' ? (
        <Image name='ActiveEmployment.svg' />
      ) : (
        <Image name='InActiveEmployment.svg' />
      );
    case 'education':
      return state === 'active' ? (
        <Image name='ActiveEducation.svg' />
      ) : (
        <Image name='InActiveEducation.svg' />
      );
    case 'worldCheck':
      return state === 'active' ? (
        <Image name='ActiveWorld.svg' />
      ) : (
        <Image name='InActiveWorld.svg' />
      );
    case 'cibilCheck':
      return state === 'active' ? (
        <Image name='ActiveCibilCheck.svg' />
      ) : (
        <Image name='InActiveCibilCheck.svg' />
      );
    case 'drugCheck':
      return state === 'active' ? (
        <Image name='ActiveDrugCheck.svg' />
      ) : (
        <Image name='InActiveDrugCheck.svg' />
      );
    case 'referenceCheck':
      return state === 'active' ? (
        <Image name='ActiveReferenceCheck.svg' />
      ) : (
        <Image name='InActiveReferenceCheck.svg' />
      );
    case 'customCheck':
      return state === 'active' ? (
        getCustomCheckSVG('#5989f0', 20, 20)
      ) : (
        <Image name='InActiveCustom.svg' />
      );
    default:
      return null;
  }
};

export const getOverallStatusSVG = (status) => {
  let colorData = {
    // 3: { status: 'AwaitingInput', color: '#c2c2c2', label: 'Interim Report' ,x:'15', y:'13' ,width:'150'},
    11: {
      status: 'ConsentMissing',
      color: '#FF8800',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    4: {
      status: 'FormSubmitted',
      color: '#0099cc',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    0: {
      status: 'InProgress',
      color: '#0d47a1',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    1: { status: 'Verified', color: '#21d549', label: 'Completed', x: '17', y: '13', width: '90' },
    6: {
      status: 'CompletedWithException',
      color: '#ff8800',
      label: 'Completed With Exception',
      x: '12',
      y: '13',
      width: '150'
    },
    9: {
      status: 'OnHold',
      color: '#979797',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    10: {
      status: 'Cancelled',
      color: '#979797',
      label: 'Cancelled',
      x: '25',
      y: '13',
      width: '100'
    },
    8: { status: 'Closed', color: '#979797', label: 'Closed', x: '32', y: '13', width: '100' },
    12: {
      status: 'InsufficientFunds',
      color: '#979797',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    }
  };
  let colorOptions = colorData[status] || null;
  return (
    <>
      {colorOptions ? (
        <svg
          width='160'
          height='21'
          viewBox='0 0 150 21'
          version='1.1'
          xmlns='http://www.w3.org/2000/svg'
        >
          <title>Group 9</title>
          <desc>Created with Sketch.</desc>
          <g id='30-July' stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
            <g id='CheckList_Icons_Final' transform='translate(-432, -683.000000)'>
              <g id='AwaitingInput' transform='translate(427.000000, 683.102273)'>
                <g id='Group-9'>
                  <rect width={colorOptions.width} height='20' rx='10' fill={colorOptions.color} />
                  <text
                    id='Awaiting-Input'
                    fontFamily='Montserrat, sans-serif'
                    fontSize='9'
                    fontWeight='600'
                    fill='#FFFFFF'
                  >
                    <tspan x={colorOptions.x} y={colorOptions.y}>
                      {colorOptions.label}
                    </tspan>
                  </text>
                </g>
              </g>
            </g>
          </g>
        </svg>
      ) : null}
    </>
  );
};

export const getCityNameWithoutAsterisk = (data) => {
  if (!isEmpty(data) && data.endsWith('*')) {
    return data.slice(0, -1);
  }
  return data;
};

export const capitalizeCase = (letter) => {
  return letter[0]?.toUpperCase() + letter.slice(1).toLowerCase();
};

export const getDocPipingUrl = (uuid) => {
  return `${AppConstants.baseURL}candidate/document?uuid=${uuid}`;
};

export const getDocPipingUrlCA = (uuid, checkType) => {
  const isSA = ADMIN_URL.some((str) => AppConstants.baseURL.toString().includes(str));
  return `${
    isSA ? AppConstants.caBaseURL : AppConstants.baseURL
  }candidate/document?uuid=${uuid}&checkType=${checkType}`;
};

export const isRegularDate = (date) => {
  return moment(date, INPUT_VALUE_DATEFORMAT, true).isValid();
};

export const removeAsterisks = (str) => {
  return !isEmpty(str) ? str.replace(/\*/g, '') : '';
};

export const getFormattedDate = (date, isReport = false, MYformat = false) => {
  let formattedDate = '';
  if (moment(date, ['MMM YYYY', 'MMMM YYYY', 'YYYY', 'YYYY-MM-DD', 'YYYY-MM'], true).isValid()) {
    if (isReport && MYformat && moment(date, ['YYYY-MM-DD', 'YYYY-MM'], true).isValid()) {
      formattedDate = moment(date).format(displayMonthAndYearFormat);
      return formattedDate;
    }
    if (moment(date, 'YYYY-MM-DD', true).isValid()) {
      formattedDate = moment(date).format(isReport ? displayDateReportFormat : 'YYYY-MM-DD');
    } else if (moment(date, 'MMM YYYY', true).isValid()) {
      formattedDate = moment(date).format('MMM YYYY');
    } else if (moment(date, 'MMMM YYYY', true).isValid()) {
      formattedDate = moment(date).format('MMMM YYYY');
    } else if (moment(date, 'YYYY', true).isValid()) {
      formattedDate = moment(date).format('YYYY');
    } else if (moment(date, 'YYYY-MM', true).isValid()) {
      formattedDate = moment(date).format(isReport ? displayMonthAndYearFormat : 'YYYY-MM');
    }
  }
  return formattedDate;
};

// Covert Blob to Pdf and download
export const generatePdf = (data, fileName, setState = null) => {
  try {
    const pdfBlob = new Blob([data]);
    const dataURL = URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = dataURL;
    downloadLink.download = (fileName || 'Candidate') + '_Report.pdf';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(dataURL);
    toast.success('Report downloaded successfully.');
    if (setState) setState(false);
  } catch (err) {
    console.error('Generate PDF Error:::', err);
    if (setState) setState(false);
  }
};

// Covert to comma separated string
export const convertToCommaSeparateString = (value) => {
  if (value) {
    return parseInt(value.replace(/,/g, '')).toLocaleString('en-IN');
  } else {
    return '';
  }
};

/**
 *  Function ot get label by value
 * @param {String} value
 * @param {Object} obj
 * @returns
 */
export const getLabelByValue = (value, obj) => {
  const val = obj.find((course) => course.value === value);
  return val ? val.label : '';
};
